import { Footer } from '../components/Footer';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { trackView, trackConversion } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, CheckCircle2, ChevronLeft, ChevronRight, User, UsersRound, Phone, MoreHorizontal, ClipboardCheck, Loader2, Link2Off, Search, ShieldCheck } from 'lucide-react';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';
import { emptyAddressFields, parseGooglePlaceAddress } from '@/lib/address';
import { calcAgeNum } from '@/lib/helpers';
import { formatBrazilDate } from '@/lib/timezone';

const API = `${window.location.origin}/api`;

const FONT_MAP = {
  'Inter': "'Inter', sans-serif",
  'Georgia': "'Georgia', serif",
  'Playfair Display': "'Playfair Display', serif",
  'Roboto Mono': "'Roboto Mono', monospace",
  'Poppins': "'Poppins', sans-serif",
};

const BG_PRESETS = {
  lavender_mist: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0e7fe 100%)',
  rose_quartz: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)',
  morning_sky: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
  sage_garden: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf9 100%)',
  warm_sand: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)',
  pearl: 'linear-gradient(180deg, #fafafa 0%, #f5f5f4 50%, #fafaf9 100%)',
  soft_lilac: 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 60%, #faf5ff 100%)',
  ocean_breeze: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 50%, #eff6ff 100%)',
  blush_peach: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fef2f2 100%)',
  mint_cream: 'linear-gradient(180deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)',
  twilight: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)',
  midnight: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
};

function getPageBgStyle(page) {
  if (!page) return { background: 'linear-gradient(to bottom right, #f8fafc, #eff6ff)' };
  const bgType = page.bg_type || 'solid';
  if (bgType === 'gradient') {
    const dir = page.bg_gradient_direction || 'to bottom';
    return { background: `linear-gradient(${dir}, ${page.cor_fundo || '#f8fafc'}, ${page.cor_fundo_2 || '#e2e8f0'})` };
  }
  if (bgType === 'preset' && page.bg_preset && BG_PRESETS[page.bg_preset]) {
    return { background: BG_PRESETS[page.bg_preset] };
  }
  return { backgroundColor: page.cor_fundo || '#f8fafc' };
}

const INSTRUMENTOS = [
  'Acordeon', 'Clarinete', 'Clarinete Alto', 'Clarinete Baixo', 'Corne Inglês',
  'Eufonio', 'Fagote', 'Flauta', 'Flugelhorn', 'Oboé', 'Piano',
  'Sax Alto', 'Sax Barítono', 'Sax Soprano', 'Sax Tenor',
  'Trombone', 'Trompa', 'Trompete', 'Tuba',
  'Viola', 'Violão', 'Violino', 'Violoncelo'
];

const WIZARD_STEPS = [
  { label: 'Pessoais', icon: User },
  { label: 'Familia', icon: UsersRound },
  { label: 'Contato', icon: Phone },
  { label: 'Outros', icon: MoreHorizontal },
  { label: 'Resumo', icon: ClipboardCheck },
];

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

function calcAge(dateStr) {
  return calcAgeNum(dateStr);
}

function getParticipantLabel(age) {
  if (age === null) return { nome: 'jovem ou crianca', artigo: 'o(a)', do: 'do(a)' };
  if (age >= 12) return { nome: 'jovem', artigo: 'o', do: 'do' };
  return { nome: 'crianca', artigo: 'a', do: 'da' };
}

function ProgressBar({ step, accentColor, accentTextColor }) {
  return (
    <div className="px-1 pt-2 pb-4" data-testid="progress-bar-atualizar">
      <div className="flex items-center justify-between mb-3">
        {WIZARD_STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <div key={i} className="flex flex-col items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${!done && !active ? 'bg-slate-100 text-slate-400' : ''}`}
                style={done || active ? { backgroundColor: accentColor || '#2563eb', color: accentTextColor || '#fff', ...(active ? { boxShadow: `0 0 0 4px ${(accentColor || '#2563eb') + '22'}` } : {}) } : undefined}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-[9px] mt-1 text-center leading-tight ${active ? 'font-semibold' : 'text-slate-400'}`} style={active ? { color: accentColor || '#2563eb' } : undefined}>{s.label}</span>
            </div>
          );
        })}
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${((step + 1) / WIZARD_STEPS.length) * 100}%`, backgroundColor: accentColor || '#2563eb' }} />
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-b-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right max-w-[60%]">{value}</span>
    </div>
  );
}

const EMPTY_FORM = {
  nome_completo: '', genero: '', data_nascimento: '', email: '', whatsapp: '',
  whatsapp_pai: '', whatsapp_mae: '',
  tem_whatsapp_proprio: null, tem_email_proprio: null,
  batizado: false, selado: false, nome_pai: '', nome_mae: '',
  ...emptyAddressFields(),
  auxiliar_jovens: false, grupo_visita: false,
  organista: false, instrutora_musicalizacao: false, integrante_ebi: false,
  musico: false, instrumentos: [], instrutor_gem: false, aluno_gem: false
};

export default function PublicAtualizar() {
  const { slug } = useParams();

  const [pageLoading, setPageLoading] = useState(true);
  const [invalidSlug, setInvalidSlug] = useState(false);
  const [settings, setSettings] = useState({ app_name: 'CCBRJM', subtitle: '' });
  const [churchSlug, setChurchSlug] = useState(slug || null);
  const [pageStyle, setPageStyle] = useState(null);

  // Flow: 'identify' | 'verify' | 'wizard'
  const [phase, setPhase] = useState('identify');
  const [verifiedPessoaId, setVerifiedPessoaId] = useState(null);

  // Identify
  const [inputNome, setInputNome] = useState('');
  const [searching, setSearching] = useState(false);

  // Verify
  const [verifyDob, setVerifyDob] = useState('');
  const [verifyMae, setVerifyMae] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Wizard
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [direction, setDirection] = useState('next');

  const age = calcAge(form.data_nascimento);
  const participant = getParticipantLabel(age);
  const isOver14 = age !== null && age >= 14;

  useEffect(() => {
    const fetchChurchInfo = async () => {
      setPageLoading(true);
      try {
        if (slug) {
          const r = await fetch(`${API}/churches/${slug}/public`);
          if (!r.ok) { setInvalidSlug(true); return; }
          const data = await r.json();
          setSettings({ app_name: data.app_name, subtitle: data.subtitle });
          setChurchSlug(slug);
          if (data.page_style) setPageStyle(data.page_style);
          document.title = 'CCBRJM';
          trackView('atualizar', slug, '');
        }
      } catch { setInvalidSlug(true); }
      finally { setPageLoading(false); }
    };
    fetchChurchInfo();
  }, [slug]);

  // --- Step 1: Name search ---
  const handleNameSearch = async () => {
    if (inputNome.trim().length < 3) { toast.error('Digite pelo menos 3 caracteres do seu nome.'); return; }
    setSearching(true);
    try {
      const r = await fetch(`${API}/public/atualizar-buscar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: inputNome.trim(), church_slug: churchSlug }),
      });
      if (!r.ok) { toast.error('Erro ao buscar. Tente novamente.'); return; }
      const data = await r.json();
      if (data.found) {
        setPhase('verify');
      } else {
        toast.error('Nome nao encontrado. Verifique se digitou corretamente.');
      }
    } catch { toast.error('Erro de conexao. Tente novamente.'); }
    finally { setSearching(false); }
  };

  // --- Step 2: Verify DOB + Mother ---
  const handleVerify = async () => {
    if (!verifyDob) { toast.error('Informe a data de nascimento.'); return; }
    if (!verifyMae.trim()) { toast.error('Informe o nome da mae.'); return; }
    setVerifying(true);
    try {
      const r = await fetch(`${API}/public/atualizar-verificar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: inputNome.trim(), data_nascimento: verifyDob, nome_mae: verifyMae.trim(), church_slug: churchSlug }),
      });
      if (!r.ok) {
        let msg = 'Dados nao encontrados. Verifique as informacoes e tente novamente.';
        try { const err = await r.json(); msg = err.detail || msg; } catch (parseError) { console.warn('Falha ao ler erro de verificação', parseError); }
        toast.error(msg);
        return;
      }
      const pessoa = await r.json();
      setVerifiedPessoaId(pessoa.pessoa_id);
      prefillForm(pessoa);
      setPhase('wizard');
      toast.success('Identidade verificada! Edite seus dados.');
    } catch { toast.error('Erro de conexao. Tente novamente.'); }
    finally { setVerifying(false); }
  };

  const prefillForm = (p) => {
    if (!p) return;
    setForm({
      nome_completo: p.nome_completo || '',
      genero: p.genero || '',
      data_nascimento: p.data_nascimento || '',
      email: p.email || '',
      whatsapp: p.whatsapp || '',
      whatsapp_pai: p.whatsapp_pai || '',
      whatsapp_mae: p.whatsapp_mae || '',
      endereco: p.endereco || p.endereco_formatado || '', endereco_formatado: p.endereco_formatado || p.endereco || '', logradouro: p.logradouro || '', numero: p.numero || '', bairro: p.bairro || '', cidade: p.cidade || '', uf: p.uf || '', cep: p.cep || '', complemento: p.complemento || '', lat: p.lat ?? null, lng: p.lng ?? null, google_place_id: p.google_place_id || '',
      tem_whatsapp_proprio: p.whatsapp ? true : null,
      tem_email_proprio: p.email ? true : null,
      batizado: p.batizado || false,
      selado: p.selado || false,
      nome_pai: p.nome_pai || '',
      nome_mae: p.nome_mae || '',
      auxiliar_jovens: p.auxiliar_jovens || false,
      grupo_visita: p.grupo_visita || false,
      organista: p.organista || false,
      instrutora_musicalizacao: p.instrutora_musicalizacao || false,
      integrante_ebi: p.integrante_ebi || false,
      musico: p.musico || false,
      instrumentos: p.instrumentos || [],
      instrutor_gem: p.instrutor_gem || false,
      aluno_gem: p.aluno_gem || false,
    });
  };

  // --- Wizard ---
  const toggleInstrumento = (inst) => setForm(p => ({
    ...p, instrumentos: p.instrumentos.includes(inst) ? p.instrumentos.filter(i => i !== inst) : [...p.instrumentos, inst]
  }));

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.nome_completo.trim()) e.nome_completo = 'Obrigatorio';
      if (!form.genero) e.genero = 'Obrigatorio';
      if (!form.data_nascimento) e.data_nascimento = 'Obrigatorio';
    } else if (s === 1) {
      if (!form.nome_pai.trim()) e.nome_pai = 'Obrigatorio';
      if (!form.whatsapp_pai || form.whatsapp_pai.replace(/\D/g, '').length < 11) e.whatsapp_pai = 'WhatsApp obrigatorio (11 digitos)';
      if (!form.nome_mae.trim()) e.nome_mae = 'Obrigatorio';
      if (!form.whatsapp_mae || form.whatsapp_mae.replace(/\D/g, '').length < 11) e.whatsapp_mae = 'WhatsApp obrigatorio (11 digitos)';
    } else if (s === 2) {
      if (isOver14) {
        if (!form.whatsapp || form.whatsapp.replace(/\D/g, '').length < 11) e.whatsapp = 'WhatsApp obrigatorio para maiores de 14 anos';
        if (!form.email || !EMAIL_REGEX.test(form.email)) e.email = 'E-mail obrigatorio para maiores de 14 anos';
      } else {
        if (form.tem_whatsapp_proprio === null) e.tem_whatsapp_proprio = 'Selecione uma opcao';
        if (form.tem_whatsapp_proprio === true && (!form.whatsapp || form.whatsapp.replace(/\D/g, '').length < 11)) e.whatsapp = 'WhatsApp obrigatorio (11 digitos)';
        if (form.tem_email_proprio === null) e.tem_email_proprio = 'Selecione uma opcao';
        if (form.tem_email_proprio === true && (!form.email || !EMAIL_REGEX.test(form.email))) e.email = 'Email obrigatorio e valido';
      }
    } else if (s === 3) {
      if (form.musico && form.instrumentos.length === 0) e.instrumentos = 'Selecione ao menos um instrumento';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) { toast.error('Corrija os campos destacados'); return; }
    setErrors({}); setDirection('next'); setStep(s => Math.min(s + 1, WIZARD_STEPS.length - 1));
  };

  const goBack = () => {
    if (step === 0) {
      setPhase('verify');
      return;
    }
    setErrors({}); setDirection('back'); setStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) { toast.error('Corrija os campos destacados'); return; }
    setLoading(true);
    try {
      const body = {
        nome_completo: form.nome_completo.trim(), genero: form.genero, data_nascimento: form.data_nascimento,
        email: form.email || null, whatsapp: form.whatsapp || null,
        whatsapp_pai: form.whatsapp_pai || null, whatsapp_mae: form.whatsapp_mae || null,
        endereco: form.endereco || '', endereco_formatado: form.endereco_formatado || form.endereco || '', logradouro: form.logradouro || '', numero: form.numero || '', bairro: form.bairro || '', cidade: form.cidade || '', uf: form.uf || '', cep: form.cep || '', complemento: form.complemento || '', lat: form.lat, lng: form.lng, google_place_id: form.google_place_id || '',
        batizado: form.batizado, selado: form.selado,
        nome_pai: form.nome_pai.trim(), nome_mae: form.nome_mae.trim(),
        auxiliar_jovens: form.auxiliar_jovens, grupo_visita: form.grupo_visita,
        organista: form.organista, instrutora_musicalizacao: form.instrutora_musicalizacao,
        integrante_ebi: form.integrante_ebi, musico: form.musico, instrumentos: form.instrumentos,
        instrutor_gem: form.instrutor_gem, aluno_gem: form.aluno_gem, church_slug: churchSlug,
      };
      const r = await fetch(`${API}/public/update-registration/${verifiedPessoaId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) {
        let msg = 'Erro ao enviar atualizacao';
        try { const err = await r.json(); msg = err.detail || msg; } catch (parseError) { console.warn('Falha ao ler erro de atualização', parseError); }
        throw new Error(msg);
      }
      setSubmitted(true); toast.success('Atualizacao enviada para aprovacao!'); trackConversion('atualizar', slug, '');
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  };

  const resetAll = () => {
    setSubmitted(false); setForm(EMPTY_FORM); setStep(0); setErrors({});
    setPhase('identify'); setVerifiedPessoaId(null);
    setInputNome(''); setVerifyDob(''); setVerifyMae('');
  };

  // --- Style ---
  const bgStyle = getPageBgStyle(pageStyle);
  const fontFamily = pageStyle?.fonte ? (FONT_MAP[pageStyle.fonte] || FONT_MAP['Inter']) : undefined;
  const titleColor = pageStyle?.cor_fonte_nome || undefined;
  const bioColor = pageStyle?.cor_fonte_bio || undefined;
  const iconColor = pageStyle?.cor_icone || undefined;
  const iconBg = pageStyle?.cor_fundo_icone || undefined;
  const btnStyle = { backgroundColor: iconBg || '#2563eb', color: iconColor || '#ffffff' };
  const boolLabel = (v) => v ? 'Sim' : 'Nao';

  if (pageLoading) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <div className="text-slate-500" data-testid="atualizar-loading">Carregando...</div>
    </div>
  );

  if (invalidSlug) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center" data-testid="atualizar-invalid-slug">
        <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Link2Off className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-700 mb-1">Pagina nao encontrada</h2>
        <p className="text-sm text-slate-500">O link que voce acessou nao existe ou foi desativado.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <Card className="w-full max-w-md p-8 bg-white/90 backdrop-blur-sm shadow-xl border-0 text-center" data-testid="atualizar-success">
        <div className="w-20 h-20 bg-green-50 rounded-full mx-auto mb-6 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-3" style={{ fontFamily, color: titleColor || '#1e293b' }}>
          Atualizacao Enviada!
        </h2>
        <p className="text-slate-600 mb-6" style={{ color: bioColor || undefined }}>
          Sua atualizacao foi enviada para aprovacao. O administrador ira analisar as alteracoes.
        </p>
        <Button onClick={resetAll} variant="outline" data-testid="atualizar-restart-btn">Voltar ao Inicio</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4" style={{ ...bgStyle, fontFamily }}>
      <div className="max-w-lg w-full">
        <Card className="bg-white/90 backdrop-blur-sm shadow-xl border-0 overflow-hidden" data-testid="atualizar-form-card">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center shadow-lg" style={{ backgroundColor: iconBg || '#2563eb' }}>
              <Users className="w-7 h-7" style={{ color: iconColor || '#ffffff' }} />
            </div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily, color: titleColor || '#1e293b' }}>{settings.app_name}</CardTitle>
            <CardDescription style={{ color: bioColor || undefined }}>
              {phase === 'identify' && 'Atualize seus dados cadastrais'}
              {phase === 'verify' && 'Verificacao de identidade'}
              {phase === 'wizard' && 'Edite seus dados'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* PHASE: IDENTIFY */}
            {phase === 'identify' && (
              <div className="space-y-5 py-4" data-testid="phase-identify" style={{ animation: 'slideIn 0.3s ease-out' }}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: (iconBg || '#2563eb') + '18' }}>
                    <Search className="w-7 h-7" style={{ color: iconBg || '#2563eb' }} />
                  </div>
                  <p className="text-sm text-slate-600">
                    Digite seu nome completo para localizar seu cadastro.
                  </p>
                </div>
                <div>
                  <Label>Nome Completo</Label>
                  <Input value={inputNome} onChange={(e) => setInputNome(e.target.value)}
                    placeholder="Digite seu nome completo" data-testid="input-nome-busca"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNameSearch(); }} />
                </div>
                <Button onClick={handleNameSearch} disabled={searching || inputNome.trim().length < 3}
                  data-testid="btn-buscar-nome" className="w-full" style={btnStyle}>
                  {searching ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Buscando...</> : 'Continuar'}
                </Button>
              </div>
            )}

            {/* PHASE: VERIFY */}
            {phase === 'verify' && (
              <div className="space-y-5 py-4" data-testid="phase-verify-atualizar" style={{ animation: 'slideIn 0.3s ease-out' }}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: (iconBg || '#2563eb') + '18' }}>
                    <ShieldCheck className="w-7 h-7" style={{ color: iconBg || '#2563eb' }} />
                  </div>
                  <p className="text-sm text-slate-600">
                    Para sua seguranca, confirme seus dados abaixo.
                  </p>
                </div>
                <div>
                  <Label>Data de Nascimento</Label>
                  <Input type="date" value={verifyDob} onChange={(e) => setVerifyDob(e.target.value)}
                    data-testid="input-verify-dob" />
                </div>
                <div>
                  <Label>Nome da Mae</Label>
                  <Input value={verifyMae} onChange={(e) => setVerifyMae(e.target.value)}
                    placeholder="Digite o nome da mae (parcial ou completo)" data-testid="input-verify-mae" 
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }} />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setPhase('identify'); setVerifyDob(''); setVerifyMae(''); }}
                    data-testid="btn-verify-back" className="gap-1">
                    <ChevronLeft className="w-4 h-4" /> Voltar
                  </Button>
                  <Button onClick={handleVerify} disabled={verifying || !verifyDob || !verifyMae.trim()}
                    data-testid="btn-verificar" className="flex-1" style={btnStyle}>
                    {verifying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verificando...</> : 'Verificar'}
                  </Button>
                </div>
              </div>
            )}

            {/* PHASE: WIZARD */}
            {phase === 'wizard' && (
              <>
                <div className="mb-3 px-3 py-2 rounded-lg" style={{ backgroundColor: (iconBg || '#2563eb') + '12', border: `1px solid ${(iconBg || '#2563eb') + '33'}` }}>
                  <p className="text-xs font-medium text-center" style={{ color: iconBg || '#2563eb' }}>Modo Atualizacao — Edite os campos que deseja alterar</p>
                </div>
                <ProgressBar step={step} accentColor={iconBg} accentTextColor={iconColor} />
                <div key={step} style={{ animation: `${direction === 'next' ? 'slideIn' : 'slideBack'} 0.3s ease-out` }}>

                  {step === 0 && (
                    <div className="space-y-4" data-testid="atualizar-step-0">
                      <p className="text-sm text-slate-500 mb-2">Dados pessoais.</p>
                      <div>
                        <Label>Nome Completo *</Label>
                        <Input value={form.nome_completo} onChange={(e) => setForm({ ...form, nome_completo: e.target.value })} data-testid="input-nome-edit" className={errors.nome_completo ? 'border-red-500' : ''} />
                        {errors.nome_completo && <p className="text-xs text-red-500 mt-1">{errors.nome_completo}</p>}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Genero *</Label>
                          <Select value={form.genero} onValueChange={(v) => setForm(prev => ({ ...prev, genero: v }))}>
                            <SelectTrigger data-testid="select-genero-edit" className={errors.genero ? 'border-red-500' : ''}><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="feminino">Feminino</SelectItem></SelectContent>
                          </Select>
                          {errors.genero && <p className="text-xs text-red-500 mt-1">{errors.genero}</p>}
                        </div>
                        <div>
                          <Label>Data de Nascimento *</Label>
                          <Input type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} data-testid="input-dob-edit" className={errors.data_nascimento ? 'border-red-500' : ''} />
                          {errors.data_nascimento && <p className="text-xs text-red-500 mt-1">{errors.data_nascimento}</p>}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-4" data-testid="atualizar-step-1">
                      <p className="text-sm text-slate-500 mb-2">Dados dos pais ou responsaveis.</p>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Pai</p>
                        <div className="grid grid-cols-[3fr_2fr] gap-3">
                          <div><Label>Nome completo *</Label><Input value={form.nome_pai} onChange={(e) => setForm({ ...form, nome_pai: e.target.value })} data-testid="input-pai-edit" className={errors.nome_pai ? 'border-red-500' : ''} />{errors.nome_pai && <p className="text-xs text-red-500 mt-1">{errors.nome_pai}</p>}</div>
                          <div><Label>WhatsApp *</Label><Input value={form.whatsapp_pai} onChange={(e) => setForm({ ...form, whatsapp_pai: formatPhone(e.target.value) })} data-testid="input-whatsapp-pai-edit" className={errors.whatsapp_pai ? 'border-red-500' : ''} />{errors.whatsapp_pai && <p className="text-xs text-red-500 mt-1">{errors.whatsapp_pai}</p>}</div>
                        </div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Mae</p>
                        <div className="grid grid-cols-[3fr_2fr] gap-3">
                          <div><Label>Nome completo *</Label><Input value={form.nome_mae} onChange={(e) => setForm({ ...form, nome_mae: e.target.value })} data-testid="input-mae-edit" className={errors.nome_mae ? 'border-red-500' : ''} />{errors.nome_mae && <p className="text-xs text-red-500 mt-1">{errors.nome_mae}</p>}</div>
                          <div><Label>WhatsApp *</Label><Input value={form.whatsapp_mae} onChange={(e) => setForm({ ...form, whatsapp_mae: formatPhone(e.target.value) })} data-testid="input-whatsapp-mae-edit" className={errors.whatsapp_mae ? 'border-red-500' : ''} />{errors.whatsapp_mae && <p className="text-xs text-red-500 mt-1">{errors.whatsapp_mae}</p>}</div>
                        </div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="public-update-address-section">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Endereço</p>
                        <div className="grid grid-cols-1 gap-3">
                          <div><Label>Endereço</Label><AddressAutocompleteInput value={form.endereco_formatado || form.endereco || ''} onChange={(value, place) => setForm(prev => ({ ...prev, ...(place ? parseGooglePlaceAddress(place, value) : { endereco: value, endereco_formatado: value, lat: null, lng: null, google_place_id: '' }) }))} placeholder="Buscar endereço..." testId="public-update-address-input" /></div>
                          <div><Label>Complemento</Label><Input value={form.complemento || ''} onChange={(e) => setForm({ ...form, complemento: e.target.value })} placeholder="Casa, bloco, apto..." data-testid="public-update-address-complement-input" /></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-5" data-testid="atualizar-step-2">
                      <p className="text-sm text-slate-500 mb-2">Informacoes de contato.</p>
                      {isOver14 ? (
                        <>
                          <div><Label>WhatsApp *</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: formatPhone(e.target.value) })} placeholder="(00) 00000-0000" data-testid="input-whatsapp-edit" className={errors.whatsapp ? 'border-red-500' : ''} />{errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}</div>
                          <div className="mt-3"><Label>E-mail *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" data-testid="input-email-edit" className={errors.email ? 'border-red-500' : ''} />{errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}</div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-700">Possui WhatsApp proprio?</p>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button type="button" variant={form.tem_whatsapp_proprio === true ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_whatsapp_proprio: true })} style={form.tem_whatsapp_proprio === true ? btnStyle : undefined}>Sim</Button>
                                <Button type="button" variant={form.tem_whatsapp_proprio === false ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_whatsapp_proprio: false, whatsapp: '' })} style={form.tem_whatsapp_proprio === false ? btnStyle : undefined}>Nao</Button>
                              </div>
                            </div>
                            {errors.tem_whatsapp_proprio && <p className="text-xs text-red-500">{errors.tem_whatsapp_proprio}</p>}
                            {form.tem_whatsapp_proprio === true && <div className="mt-2"><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: formatPhone(e.target.value) })} placeholder="(00) 00000-0000" className={errors.whatsapp ? 'border-red-500' : ''} />{errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}</div>}
                          </div>
                          <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200 mt-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-semibold text-slate-700">Possui e-mail proprio?</p>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button type="button" variant={form.tem_email_proprio === true ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_email_proprio: true })} style={form.tem_email_proprio === true ? btnStyle : undefined}>Sim</Button>
                                <Button type="button" variant={form.tem_email_proprio === false ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, tem_email_proprio: false, email: '' })} style={form.tem_email_proprio === false ? btnStyle : undefined}>Nao</Button>
                              </div>
                            </div>
                            {errors.tem_email_proprio && <p className="text-xs text-red-500">{errors.tem_email_proprio}</p>}
                            {form.tem_email_proprio === true && <div className="mt-2"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" className={errors.email ? 'border-red-500' : ''} />{errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}</div>}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4" data-testid="atualizar-step-3">
                      <p className="text-sm text-slate-500 mb-2">Marque as opcoes que se aplicam.</p>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center space-x-2"><Checkbox checked={form.batizado} onCheckedChange={(c) => setForm({ ...form, batizado: !!c })} id="at-bat" /><Label htmlFor="at-bat" className="cursor-pointer">E batizado?</Label></div>
                        <div className="flex items-center space-x-2"><Checkbox checked={form.selado} onCheckedChange={(c) => setForm({ ...form, selado: !!c })} id="at-sel" /><Label htmlFor="at-sel" className="cursor-pointer">E selado?</Label></div>
                        <div className="flex items-center space-x-2"><Checkbox checked={form.auxiliar_jovens} onCheckedChange={(c) => setForm({ ...form, auxiliar_jovens: !!c })} id="at-axj" /><Label htmlFor="at-axj" className="cursor-pointer">E auxiliar de jovens?</Label></div>
                        <div className="flex items-center space-x-2"><Checkbox checked={form.grupo_visita} onCheckedChange={(c) => setForm({ ...form, grupo_visita: !!c })} id="at-gv" /><Label htmlFor="at-gv" className="cursor-pointer">E integrante de um dos Grupos de Visita?</Label></div>
                        {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.organista} onCheckedChange={(c) => setForm({ ...form, organista: !!c })} id="at-org" /><Label htmlFor="at-org" className="cursor-pointer">E organista?</Label></div>}
                        {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.instrutora_musicalizacao} onCheckedChange={(c) => setForm({ ...form, instrutora_musicalizacao: !!c })} id="at-im" /><Label htmlFor="at-im" className="cursor-pointer">E instrutora/auxiliar da aula de musicalizacao?</Label></div>}
                        {form.genero === 'feminino' && <div className="flex items-center space-x-2"><Checkbox checked={form.integrante_ebi} onCheckedChange={(c) => setForm({ ...form, integrante_ebi: !!c })} id="at-ebi" /><Label htmlFor="at-ebi" className="cursor-pointer">E integrante do EBI (cuida das criancas)?</Label></div>}
                        {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.instrutor_gem} onCheckedChange={(c) => setForm({ ...form, instrutor_gem: !!c })} id="at-igem" /><Label htmlFor="at-igem" className="cursor-pointer">E instrutor de musica (GEM)?</Label></div>}
                        {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.aluno_gem} onCheckedChange={(c) => setForm({ ...form, aluno_gem: !!c })} id="at-agem" /><Label htmlFor="at-agem" className="cursor-pointer">E aluno da aula de musica (GEM)?</Label></div>}
                        {form.genero === 'masculino' && <div className="flex items-center space-x-2"><Checkbox checked={form.musico} onCheckedChange={(c) => setForm({ ...form, musico: !!c })} id="at-mus" /><Label htmlFor="at-mus" className="cursor-pointer">E musico?</Label></div>}
                        {form.genero === 'masculino' && form.musico && (
                          <div className="ml-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <p className="text-sm text-slate-600 mb-2">Selecione os instrumentos:</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {INSTRUMENTOS.map((inst) => (
                                <div key={inst} className="flex items-center space-x-2">
                                  <Checkbox checked={form.instrumentos.includes(inst)} onCheckedChange={() => toggleInstrumento(inst)} id={`at-inst-${inst}`} /><Label htmlFor={`at-inst-${inst}`} className="cursor-pointer text-sm">{inst}</Label>
                                </div>
                              ))}
                            </div>
                            {errors.instrumentos && <p className="text-xs text-red-500 mt-2">{errors.instrumentos}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-4" data-testid="atualizar-step-4">
                      <p className="text-sm text-slate-500 mb-2">Confira os dados antes de enviar.</p>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Dados Pessoais</p>
                        <SummaryRow label="Nome" value={form.nome_completo} />
                        <SummaryRow label="Genero" value={form.genero === 'masculino' ? 'Masculino' : 'Feminino'} />
                        <SummaryRow label="Nascimento" value={form.data_nascimento ? formatBrazilDate(form.data_nascimento, '') : ''} />
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Familia</p>
                        <SummaryRow label="Pai" value={form.nome_pai} />
                        <SummaryRow label="WhatsApp do Pai" value={form.whatsapp_pai} />
                        <SummaryRow label="Mae" value={form.nome_mae} />
                        <SummaryRow label="WhatsApp da Mae" value={form.whatsapp_mae} />
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Contato</p>
                        {form.whatsapp ? <SummaryRow label="WhatsApp" value={form.whatsapp} /> : <SummaryRow label="WhatsApp proprio" value="Nao possui" />}
                        {form.email ? <SummaryRow label="E-mail" value={form.email} /> : <SummaryRow label="E-mail proprio" value="Nao possui" />}
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Outros</p>
                        <SummaryRow label="Batizado" value={boolLabel(form.batizado)} />
                        <SummaryRow label="Selado" value={boolLabel(form.selado)} />
                        <SummaryRow label="Auxiliar de jovens" value={boolLabel(form.auxiliar_jovens)} />
                        <SummaryRow label="Grupo de Visita" value={boolLabel(form.grupo_visita)} />
                        {form.genero === 'feminino' && <SummaryRow label="Organista" value={boolLabel(form.organista)} />}
                        {form.genero === 'feminino' && <SummaryRow label="Instrutora musicalizacao" value={boolLabel(form.instrutora_musicalizacao)} />}
                        {form.genero === 'feminino' && <SummaryRow label="Integrante EBI" value={boolLabel(form.integrante_ebi)} />}
                        {form.genero === 'masculino' && <SummaryRow label="Instrutor GEM" value={boolLabel(form.instrutor_gem)} />}
                        {form.genero === 'masculino' && <SummaryRow label="Aluno GEM" value={boolLabel(form.aluno_gem)} />}
                        {form.genero === 'masculino' && <SummaryRow label="Musico" value={boolLabel(form.musico)} />}
                        {form.instrumentos.length > 0 && <SummaryRow label="Instrumentos" value={form.instrumentos.join(', ')} />}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 mt-2">
                  <Button type="button" variant="outline" onClick={goBack} data-testid="btn-back-atualizar" className="gap-1">
                    <ChevronLeft className="w-4 h-4" /> Voltar
                  </Button>
                  {step < WIZARD_STEPS.length - 1 ? (
                    <Button type="button" onClick={goNext} data-testid="btn-next-atualizar" className="gap-1" style={btnStyle}>
                      Avancar <ChevronRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleSubmit} disabled={loading} data-testid="btn-submit-atualizar" style={btnStyle}>
                      {loading ? 'Enviando...' : 'Enviar Atualizacao'}
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Footer />
      </div>
      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideBack { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
    </div>
  );
}
